/**
 * Multi-wallet management for portfolio tracking
 */

export interface TrackedWallet {
  address: string;
  label: string;
  isConnected: boolean; // true if this is the connected wallet
  addedAt: number;
}

const STORAGE_KEY = 'skop_tracked_wallets';

/**
 * Get all tracked wallets from storage
 */
export function getTrackedWallets(): TrackedWallet[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Add a wallet to track
 */
export function addTrackedWallet(address: string, label?: string): TrackedWallet[] {
  const wallets = getTrackedWallets();

  // Check if already exists
  if (wallets.some(w => w.address === address)) {
    return wallets;
  }

  const newWallet: TrackedWallet = {
    address,
    label: label || `Wallet ${wallets.length + 1}`,
    isConnected: false,
    addedAt: Date.now(),
  };

  const updated = [...wallets, newWallet];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

/**
 * Remove a tracked wallet
 */
export function removeTrackedWallet(address: string): TrackedWallet[] {
  const wallets = getTrackedWallets();
  const updated = wallets.filter(w => w.address !== address);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

/**
 * Update wallet label
 */
export function updateWalletLabel(address: string, label: string): TrackedWallet[] {
  const wallets = getTrackedWallets();
  const updated = wallets.map(w =>
    w.address === address ? { ...w, label } : w
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

/**
 * Sync connected wallet with tracked wallets
 * Marks the connected wallet and ensures it's in the list
 */
export function syncConnectedWallet(connectedAddress: string | null): TrackedWallet[] {
  let wallets = getTrackedWallets();

  // Reset all isConnected flags
  wallets = wallets.map(w => ({ ...w, isConnected: false }));

  if (connectedAddress) {
    // Check if connected wallet is tracked
    const existing = wallets.find(w => w.address === connectedAddress);

    if (existing) {
      // Mark as connected
      wallets = wallets.map(w =>
        w.address === connectedAddress ? { ...w, isConnected: true } : w
      );
    } else {
      // Add connected wallet to tracked list
      wallets.push({
        address: connectedAddress,
        label: 'My Wallet',
        isConnected: true,
        addedAt: Date.now(),
      });
    }
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(wallets));
  return wallets;
}

/**
 * Validate Solana address format
 */
export function isValidSolanaAddress(address: string): boolean {
  // Solana addresses are base58 encoded and 32-44 characters
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  return base58Regex.test(address);
}
